import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import { MAX_RATES_LIMIT, TicketingFormCategory } from "@simplyk/common";
import { useSnackbar } from "notistack";
import { type Path, useFieldArray, useFormState, useWatch, type UseFormReturn } from "react-hook-form";

import { QUICK_SEARCH_MIN_RATES } from "./QuickSearch";
import { getRateErrorEntries, type RateError } from "./helpers/rateErrors";
import { RatesQuickNav } from "./QuickSearch/RatesQuickNav";
import { Rate } from "./Rate/Rate";
import { COLLAPSIBLE_RATE_CATEGORIES } from "./Rate/shared/RateCardShell/RateCardShell";
import { RateOptionHighlightProvider } from "./RateOptionHighlight/RateOptionHighlightContext";
import { getQuestionsArchivedByRateDeletion } from "../getQuestionsArchivedByRateDeletion";
import { ControlledRatesExpansionProvider } from "./RatesExpansion/RatesExpansionContext";

import { InfiniteScroll } from "@/common/components/InfiniteScroll/InfiniteScroll";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { isMultidateTicketing } from "@/common/helpers/ticketing";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { useStableLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { AddDivider } from "@/features/LiveFormEditor/Steps/components/AddDivider";
import { useBuildDefaultRate } from "@/features/LiveFormEditor/Steps/StepTickets/hooks/useBuildDefaultRate";
import { cloneRateWithNewIds } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/helpers/cloneRate";
import { getInheritedTaxIds } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/helpers/rateTaxes";
import { RateData } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/types";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";
import { Add } from "@/icons/outlined";

const QUICK_NAV_DEFAULT_ACTION_FOOTER_HEIGHT_PX = 74;
const QUICK_NAV_BAR_HEIGHT_PX = 60;
const QUICK_NAV_GAP_THEME_SPACING = 2;
const RATES_PAGE_SIZE = 20;

export interface RatesProps {
  form: UseFormReturn<StepTicketsFormData>;
  onRateCollapsed?: (rateId: string) => void;
}

export const Rates: FC<RatesProps> = ({ form, onRateCollapsed }) => {
  const { t } = useTranslate();
  const { apiFormObject: apiTicketing, editOrCreateMode, isOnboarding } = useStableLiveFormEditorContext<"ticketing">();
  const isRegistration = Boolean(apiTicketing.donationForm?.campaignId);
  const { isSmallScreen } = useMediaQuery();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const { enqueueSnackbar } = useSnackbar();

  const { control, setFocus } = form;
  const { formCategory } = apiTicketing;
  const buildDefaultRate = useBuildDefaultRate();
  const ratesContainerRef = useRef<HTMLDivElement>(null);

  const {
    fields: rates,
    append,
    remove,
    move,
    insert,
  } = useFieldArray<StepTicketsFormData>({
    control,
    name: "rates",
    keyName: "key" as "id",
  });

  // A per-attendee question asked only for one ticket type is archived with it by the API. The card's
  // own delete confirmation names those questions, so the organizer decides before the card is removed:
  // cancelling here leaves the form untouched, where cancelling at save time could not put the card back.
  const pendingRateDeletions = useWatch({ control, name: "ratesToDelete" });
  const questionsArchivedByRateId = useMemo(() => {
    const sellableRates = (apiTicketing.rates ?? []).filter((rate) => !rate.isAddOn && !rate.parentRateId);
    const pending = pendingRateDeletions ?? [];
    const byRateId: Record<string, string[]> = {};
    for (const rate of sellableRates) {
      if (pending.includes(rate.id)) {
        continue;
      }
      const archived = getQuestionsArchivedByRateDeletion({
        questions: apiTicketing.questions ?? [],
        liveRates: sellableRates,
        rateIdsToDelete: [...pending, rate.id],
        // Includes types added but not saved yet: a replacement inherits the question before the old
        // type is archived, so swapping the only type is not a loss and must not be reported as one.
        formRateIds: rates.map((formRate) => formRate.id ?? ""),
        locale: apiTicketing.locale,
      });
      if (archived.length > 0) {
        byRateId[rate.id] = archived;
      }
    }
    return byRateId;
  }, [apiTicketing.rates, apiTicketing.questions, apiTicketing.locale, pendingRateDeletions, rates]);

  const [expandedRateId, setExpandedRateId] = useState<string | null>(null);
  const toggleExpanded = useCallback((id: string) => {
    setExpandedRateId((prev) => (prev === id ? null : id));
  }, []);

  const previousExpandedRateIdRef = useRef<string | null>(null);
  useEffect(() => {
    const previousExpandedRateId = previousExpandedRateIdRef.current;
    previousExpandedRateIdRef.current = expandedRateId;
    if (
      onRateCollapsed &&
      previousExpandedRateId &&
      previousExpandedRateId !== expandedRateId &&
      COLLAPSIBLE_RATE_CATEGORIES.has(formCategory)
    ) {
      onRateCollapsed(previousExpandedRateId);
    }
  }, [expandedRateId, onRateCollapsed, formCategory]);

  useEffect(() => {
    if (rates.length === 0) {
      append([buildDefaultRate({ ratesLength: rates.length })]);
    }
  }, [append, rates.length, buildDefaultRate]);

  useEffect(() => {
    form.setFocus("rates.0.amount");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollAndFocusRate = useCallback(
    (
      index: number,
      fieldToFocus?: Path<StepTicketsFormData>,
      options?: { delayMs?: number; block?: ScrollLogicalPosition }
    ) => {
      const run = () => {
        requestAnimationFrame(() => {
          const element = document.querySelector<HTMLElement>(`[data-index="${index}"]`);
          element?.scrollIntoView({ behavior: "smooth", block: options?.block ?? "center" });
          const fieldName = fieldToFocus ?? (`rates.${index}.amount` as Path<StepTicketsFormData>);
          const input = document.querySelector<HTMLElement>(`[name="${fieldName}"]`);
          if (input) {
            input.focus({ preventScroll: true });
          } else {
            setFocus(fieldName);
          }
        });
      };
      if (options?.delayMs) {
        window.setTimeout(run, options.delayMs);
      } else {
        run();
      }
    },
    [setFocus]
  );

  const [visibleRatesCount, setVisibleRatesCount] = useState(RATES_PAGE_SIZE);
  const [pendingErrorScroll, setPendingErrorScroll] = useState<{
    index: number;
    fieldName?: Path<StepTicketsFormData>;
  } | null>(null);

  const seenRateIdsRef = useRef(new Set<string>());
  const listRenderedRef = useRef(false);
  useEffect(() => {
    rates.forEach((rate) => rate.id && seenRateIdsRef.current.add(rate.id));
    if (rates.length > 0) {
      listRenderedRef.current = true;
    }
  }, [rates]);

  const { submitCount } = useFormState({ control });
  useEffect(() => {
    if (submitCount === 0) {
      return;
    }
    const rateErrors = form.formState.errors.rates;
    const [firstErroredEntry] = getRateErrorEntries(rateErrors);
    const firstErroredIndex = firstErroredEntry ? firstErroredEntry[0] : null;
    const erroredFieldName = firstErroredEntry
      ? getFirstErroredFieldName(firstErroredEntry[1], firstErroredEntry[0])
      : undefined;
    if (firstErroredIndex === null) {
      return;
    }
    if (COLLAPSIBLE_RATE_CATEGORIES.has(formCategory)) {
      const erroredRateId = form.getValues(`rates.${firstErroredIndex}.id`);
      if (erroredRateId) {
        setExpandedRateId(erroredRateId);
      }
    }
    setVisibleRatesCount((count) => Math.max(count, firstErroredIndex + 1));
    setPendingErrorScroll({ index: firstErroredIndex, fieldName: erroredFieldName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  useEffect(() => {
    if (!pendingErrorScroll) {
      return;
    }
    scrollAndFocusRate(pendingErrorScroll.index, pendingErrorScroll.fieldName);
    setPendingErrorScroll(null);
  }, [pendingErrorScroll, scrollAndFocusRate]);

  const addRate = useCallback(
    (position?: number) => {
      const currentRatesLength = (form.getValues("rates") ?? []).length;
      if (currentRatesLength >= MAX_RATES_LIMIT) {
        enqueueSnackbar(t("dashboard", "ticketing.maxRatesReached", { count: MAX_RATES_LIMIT }), { vibe: "danger" });
        return;
      }
      logUserActionEvent(AmplitudeEvents.FormTicketAdded);
      const newRateIndex = typeof position === "number" ? position : currentRatesLength;
      const previousRate = (form.getValues("rates") ?? [])[newRateIndex - 1];
      const newRate = {
        ...buildDefaultRate({ ratesLength: currentRatesLength }),
        taxIds: getInheritedTaxIds(previousRate, formCategory),
      };
      if (typeof position === "number") {
        insert(position, [newRate]);
      } else {
        append([newRate]);
      }
      setVisibleRatesCount((c) => Math.max(c, newRateIndex + 1));
      if (newRate.id && COLLAPSIBLE_RATE_CATEGORIES.has(formCategory)) {
        setExpandedRateId(newRate.id);
      }
      scrollAndFocusRate(newRateIndex, `rates.${newRateIndex}.name` as Path<StepTicketsFormData>, {
        delayMs: 320,
        block: "start",
      });
    },
    [append, buildDefaultRate, enqueueSnackbar, form, formCategory, insert, logUserActionEvent, scrollAndFocusRate, t]
  );

  const deleteRate = useCallback(
    (index: number) => {
      const formRates = form.getValues("rates") ?? [];
      if (formRates.length === 1) {
        return;
      }
      const id = formRates[index]?.id;
      if (id) {
        form.setValue("ratesToDelete", [...(form.getValues("ratesToDelete") ?? []), id]);
        setExpandedRateId((prev) => (prev === id ? null : prev));
      }
      remove(index);
    },
    [form, remove]
  );

  const duplicateRate = useCallback(
    (index: number) => {
      const rateToClone = (form.getValues("rates") ?? [])[index] as RateData | undefined;
      if (!rateToClone) {
        return;
      }
      const newRate = cloneRateWithNewIds(rateToClone, `${rateToClone.name} (${t("common", "copy")})`, {
        canInheritGroupSize: !isRegistration,
      });
      insert(index + 1, newRate);
      setVisibleRatesCount((c) => Math.max(c, index + 2));
      if (newRate.id && COLLAPSIBLE_RATE_CATEGORIES.has(formCategory)) {
        setExpandedRateId(newRate.id);
      }
      scrollAndFocusRate(index + 1, `rates.${index + 1}.name` as Path<StepTicketsFormData>, {
        delayMs: 320,
        block: "start",
      });
    },
    [form, formCategory, insert, isRegistration, scrollAndFocusRate, t]
  );

  const moveRateUp = useCallback(
    (index: number) => {
      if (index > 0) {
        move(index, index - 1);
      }
    },
    [move]
  );

  const moveRateDown = useCallback(
    (index: number) => {
      const length = (form.getValues("rates") ?? []).length;
      if (index < length - 1) {
        move(index, index + 1);
      }
    },
    [form, move]
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const formRates = form.getValues("rates") ?? [];
      const oldIndex = formRates.findIndex((rate) => rate.id === active.id);
      const newIndex = formRates.findIndex((rate) => rate.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        move(oldIndex, newIndex);
      }
    },
    [form, move]
  );

  const recurrences = useMemo(() => apiTicketing.recurrences ?? [], [apiTicketing.recurrences]);
  const hasMultipleDates = isMultidateTicketing(formCategory, apiTicketing.occurrences?.length ?? 0);

  const isQuickSearchVisible = !isSmallScreen && rates.length >= QUICK_SEARCH_MIN_RATES;

  const [currentMatchRateIndex, setCurrentMatchRateIndex] = useState(-1);
  const effectiveVisibleCount =
    isQuickSearchVisible && currentMatchRateIndex >= 0
      ? Math.max(visibleRatesCount, currentMatchRateIndex + 1)
      : visibleRatesCount;
  const visibleRates = rates.slice(0, effectiveVisibleCount);
  const hasMoreRates = effectiveVisibleCount < rates.length;
  const loadMoreRates = useCallback(() => {
    setVisibleRatesCount((c) => c + RATES_PAGE_SIZE);
  }, []);

  const [formSectionNode, setFormSectionNode] = useState<HTMLElement | null>(null);
  const [actionFooterHeightPx, setActionFooterHeightPx] = useState(QUICK_NAV_DEFAULT_ACTION_FOOTER_HEIGHT_PX);

  useEffect(() => {
    if (!isQuickSearchVisible) {
      setFormSectionNode(null);
      return;
    }

    setFormSectionNode(document.querySelector<HTMLElement>('[data-test="form-section"]'));

    const footer = document.querySelector<HTMLElement>('[data-test="step-action-footer"]');
    if (!footer) {
      return;
    }
    setActionFooterHeightPx(footer.offsetHeight);
    const observer = new ResizeObserver(() => setActionFooterHeightPx(footer.offsetHeight));
    observer.observe(footer);
    return () => observer.disconnect();
  }, [isQuickSearchVisible]);

  const getAddButtonLabel = () => {
    switch (formCategory) {
      case TicketingFormCategory.MembershipV2:
        return t("dashboard", "ticketing.addRateMembership");
      case TicketingFormCategory.Shop:
      case TicketingFormCategory.Custom:
        return t("dashboard", "ticketing.addRateItem");
      case TicketingFormCategory.Auction:
        return t("dashboard", "liveFormEditor.addAnotherItem");
      case TicketingFormCategory.Lottery:
        return t("dashboard", "ticketing.addRate.rate");
      case TicketingFormCategory.Sweepstakes:
        return t("dashboard", "sweepstakes.rates.addRate");
      default:
        if (apiTicketing.donationForm?.campaignId) {
          return t("dashboard", "ticketing.addRateRegistration");
        }
        return t("dashboard", "ticketing.addRate.type");
    }
  };

  const list = (
    <Box
      ref={ratesContainerRef}
      sx={
        isQuickSearchVisible
          ? (theme) => ({
              paddingBottom: `calc(${QUICK_NAV_BAR_HEIGHT_PX}px + ${theme.spacing(QUICK_NAV_GAP_THEME_SPACING * 2)})`,
            })
          : undefined
      }
    >
      <Stack>
        <InfiniteScroll hasMore={hasMoreRates} onLoadMore={loadMoreRates} isLoading={false}>
          {visibleRates.map((rate, index) => {
            const initialRate = (form.getValues("rates") ?? [])[index] as RateData | undefined;
            return (
              <React.Fragment key={rate.id}>
                <Collapse
                  in
                  appear={listRenderedRef.current && Boolean(rate.id) && !seenRateIdsRef.current.has(rate.id ?? "")}
                  timeout={300}
                  easing="ease-in-out"
                >
                  <Rate
                    rateId={rate.id ?? ""}
                    alreadyBoughtTicketsCount={initialRate?.alreadyBoughtTicketsCount ?? 0}
                    rateIndex={index}
                    formCategory={formCategory}
                    isEditing={editOrCreateMode === "edit"}
                    isOnboarding={isOnboarding}
                    isRegistration={isRegistration}
                    recurrences={recurrences}
                    hasMultipleDates={hasMultipleDates}
                    totalCardNumber={rates.length}
                    onDelete={deleteRate}
                    onDuplicate={duplicateRate}
                    onMoveUp={moveRateUp}
                    onMoveDown={moveRateDown}
                    questionsArchivedOnDelete={questionsArchivedByRateId[rate.id ?? ""]}
                  />
                </Collapse>
                {index < visibleRates.length - 1 && <AddDivider onClick={() => addRate(index + 1)} />}
              </React.Fragment>
            );
          })}
        </InfiniteScroll>
      </Stack>

      {!isQuickSearchVisible && (
        <Stack direction="row" justifyContent={isSmallScreen ? "center" : "flex-start"} marginTop={1.5}>
          <DashboardButton
            variant="outlined"
            vibe="brand"
            onClick={() => addRate()}
            startIcon={<Add />}
            data-test="add-rate-button"
          >
            {getAddButtonLabel()}
          </DashboardButton>
        </Stack>
      )}
    </Box>
  );

  const listMaybeDnd = isSmallScreen ? (
    list
  ) : (
    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={visibleRates.map((r) => r.id)} strategy={verticalListSortingStrategy}>
        {list}
      </SortableContext>
    </DndContext>
  );

  return (
    <ControlledRatesExpansionProvider
      expandedRateId={expandedRateId}
      setExpandedRateId={setExpandedRateId}
      toggleExpanded={toggleExpanded}
    >
      <RateOptionHighlightProvider>
        {isQuickSearchVisible && formSectionNode ? (
          <RatesQuickNav
            control={control}
            containerRef={ratesContainerRef}
            formSectionNode={formSectionNode}
            actionFooterHeightPx={actionFooterHeightPx}
            gapSpacingUnits={QUICK_NAV_GAP_THEME_SPACING}
            isAddDisabled={rates.length >= MAX_RATES_LIMIT}
            addLabel={getAddButtonLabel()}
            searchPlaceholder={t("dashboard", "liveFormEditor.quickSearch.placeholder")}
            counterLabel={({ current, total }) =>
              t("dashboard", "liveFormEditor.quickSearch.counter", { current, total })
            }
            onAdd={addRate}
            onCurrentMatchRateIndexChange={setCurrentMatchRateIndex}
          >
            {listMaybeDnd}
          </RatesQuickNav>
        ) : (
          listMaybeDnd
        )}
      </RateOptionHighlightProvider>
    </ControlledRatesExpansionProvider>
  );
};

const getFirstErroredFieldName = (rateError: RateError, index: number): Path<StepTicketsFormData> | undefined => {
  const firstErroredField = rateError ? Object.keys(rateError)[0] : undefined;
  return firstErroredField ? (`rates.${index}.${firstErroredField}` as Path<StepTicketsFormData>) : undefined;
};
